/**
 * Extract members from policy quotation
 * @param {Object} policy - The policy object
 * @returns {Array} - Array of members with their benefits and premiums
 */
export const extractMembersFromPolicy = (policy) => {
  if (!policy || !policy.quotation || !policy.quotation.policy_schedule_entries) {
    return [];
  }

  return policy.quotation.policy_schedule_entries.map(entry => {
    // Check if entry has a nested dependant structure or flat structure
    if (entry.dependant) {
      // Nested structure (from policy API response)
      return {
        ...entry.dependant,
        benefits: entry.benefits || [],
        premium_amount_in_cents: entry.premium_amount_in_cents || 0,
        uuid: entry.dependant.additional_details && entry.dependant.additional_details.uuid
          ? entry.dependant.additional_details.uuid
          : entry.dependant.guid,
        type: entry.type || (entry.dependant.additional_details && entry.dependant.additional_details.memberType)
      };
    } else {
      // Flat structure (already in correct format)
      return {
        ...entry,
        uuid: entry.uuid || entry.guid
      };
    }
  });
};

/**
 * Sort nuclear members: Principal first, then Spouse, then Children
 * @param {Array} members - Array of nuclear member objects
 * @returns {Array} - Sorted array
 */
const sortNuclearMembers = (members) => {
  const order = { 'Principal': 1, 'Spouse': 2, 'Child': 3 };
  return members.sort((a, b) => {
    return (order[a.relationship] || 999) - (order[b.relationship] || 999);
  });
};

/**
 * Group members by their category
 * @param {Array} members - Array of member objects
 * @returns {Object} - Object with nuclear, parents, and extended arrays
 */
export const groupMembers = (members) => {
  const nuclear = members.filter(m =>
    ['Principal', 'Spouse', 'Child'].includes(m.relationship)
  );

  const parents = members.filter(m =>
    ['Parent', 'Parent_In_Law'].includes(m.relationship)
  );

  const extended = members.filter(m =>
    !['Principal', 'Spouse', 'Child', 'Parent', 'Parent_In_Law'].includes(m.relationship)
  );

  return {
    nuclear: sortNuclearMembers(nuclear),
    parents,
    extended
  };
};

/**
 * Determine if removing members will change coverage type
 * @param {Array} remainingMembers - Members that will remain
 * @returns {Object} - { willChange: boolean, newCoverageType: string }
 */
export const checkCoverageTypeChange = (remainingMembers) => {
  const nonPrincipalCount = remainingMembers.filter(
    m => m.relationship !== 'Principal'
  ).length;

  if (nonPrincipalCount === 0) {
    return {
      willChange: true,
      newCoverageType: 'Member Only'
    };
  }

  return {
    willChange: false,
    newCoverageType: 'Member & Family'
  };
};

/**
 * Check if a member can be removed
 * @param {Object} member - The member to check
 * @returns {Object} - { canRemove: boolean, reason: string }
 */
export const canRemoveMember = (member) => {
  if (member.relationship === 'Principal') {
    return {
      canRemove: false,
      reason: 'Principal member cannot be removed from the policy'
    };
  }

  return {
    canRemove: true,
    reason: null
  };
};
